import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { Banner, type BannerColors } from '@sunwebgroup/uds-ui/banner'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { updatePerRoomService } from '@/scopes/checkout/domain/actions'
import { createAncillariesClickedEvent } from '@/scopes/checkout/domain/analytics'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { bannerColorMap, ServiceType } from '@/scopes/checkout/domain/types'
import { BannerType } from '@/scopes/common/domain/types/banner'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from '../GenericService/GenericService.module.css'
import serviceStyles from '../ServiceComponents.module.css'

export function MealplanDPService({ item }: { item: ServiceItem }) {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.MealplanDP)
  const { sendEvent } = useAnalytics()
  const { componentTitle, componentDescription, popupTitle, popupDescription, popupReadMoreLabel } = item

  useSendAncillaryAvailable(service, 'selectMealplan')

  const handleValueChange = React.useCallback(
    (roomId: string, value: string) => {
      if (!service)
        return
      setServiceValue(updatePerRoomService({ roomId, value })(service))

      const optionClicked = service.options[value]
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label ? `${optionClicked?.label} for room ${roomId}` : '',
          description: 'selectMealplan',
          element: 'radiobutton',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { rooms, banners } = service

  return (
    <CheckoutSection
      title={componentTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_mealplan_dp_title' })}
      description={componentDescription || intl.formatMessage({ id: 'checkout_steps_ancillaries_mealplan_dp_description' })}
      glyph={getIconForService(ServiceType.MealplanDP)}
      moreInformationTitle={popupTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_mealplan_dp_modal_title' })}
      moreInformationDescription={popupDescription || intl.formatMessage({ id: 'checkout_steps_ancillaries_mealplan_dp_modal_description' })}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      <div className={cn(styles.flexContent, styles.flexContentGap12)}>
        {Object.values(rooms ?? {}).map(room => (
          <RoomRow
            key={room.id}
            {...room}
            onValueChange={handleValueChange}
          />
        ))}

        {banners.map(({ title, text, type = 'info' }, index) => (
          <Banner
            key={`banner-${index}`}
            color={bannerColorMap[type as BannerType] as BannerColors}
            size="M"
          >
            <Banner.TextsWrapper>
              {title && <Banner.Text>{title}</Banner.Text>}
              {text && <Banner.SupportingText innerHTML={text} />}
            </Banner.TextsWrapper>
          </Banner>
        ))}
      </div>
    </CheckoutSection>
  )
}

function RoomRow({
  id,
  availableOptions,
  selectedValue,
  name,
  onValueChange,
}: {
  id: string
  availableOptions?: string[]
  selectedValue?: string
  name: string
  onValueChange: (roomId: string, value: string) => void
}) {
  const intl = useIntl()
  const { service } = useService(ServiceType.MealplanDP)

  const handleValueChange = React.useCallback(
    (value: string) => {
      onValueChange(id, value)
    },
    [id, onValueChange],
  )

  if (!service)
    return null

  const { options, rooms } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap16)} key={id}>
      {Object.values(rooms ?? {}).length > 1 && (
        <Typography as="p" className={serviceStyles.roomName}>
          {name}
        </Typography>
      ) }
      <SelectableRadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <div className={cn(styles.flexContent, styles.flexContentGap8)}>
          {(availableOptions ?? []).map((optionId) => {
            const { value, label, badge, price } = options[optionId]

            return (
              <div className={styles.itemOptionWrapper} key={optionId}>
                <SelectableListItem value={value}>
                  <div className={styles.itemOption}>
                    <div>
                      <RadioButton value={value} />
                    </div>
                    <div>
                      {badge && <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>{badge}</Tag>}
                      <span className={styles.optionValue}>{label}</span>
                    </div>
                    <div className={styles.itemOptionTrailing}>
                      <span className={styles.optionPrice}>
                        {intl.formatCurrency(price, { showSign: price < 0 })}
                      </span>
                    </div>
                  </div>
                </SelectableListItem>
              </div>
            )
          })}
        </div>
      </SelectableRadioButtonGroup>
    </div>
  )
}
